import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, LogOut, Building2, ClipboardCheck, Users, TrendingUp, DollarSign, RefreshCw, Check, X, Plus, Minus, Trash2, Radar as RadarIcon, Search, Power, Ticket, Copy, CreditCard, Bot, BotOff, Download, KeyRound, Briefcase, Phone, MapPin, CheckCircle2, Clock, AlertTriangle, Trophy, Gift } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/hola/PasswordInput";
import AdminCouponsPanel from "@/components/hola/AdminCouponsPanel";
import AdminGoldPanel from "@/components/hola/AdminGoldPanel";
import AdminRafflePanel, { AuraVoiceToggle } from "@/components/hola/AdminRafflePanel";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function adminApi() {
  const inst = axios.create({ baseURL: `${BACKEND_URL}/api/admin` });
  inst.interceptors.request.use((c) => {
    const t = localStorage.getItem("holaradar_admin_token");
    if (t) c.headers.Authorization = `Bearer ${t}`;
    return c;
  });
  return inst;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState("stats"); // stats | merchants | transfers | matches
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi().get("/me");
        setAdmin(data);
      } catch {
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("holaradar_admin_token");
    navigate("/admin/login");
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-neutral-500">Cargando panel admin…</div>;
  if (!admin) return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-[#0A0A0A] text-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#FF007A] grid place-items-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <div className="font-heading font-extrabold">Panel Admin · HOLA RADAR</div>
              <div className="text-[11px] text-neutral-400" data-testid="admin-name">{admin.name}</div>
            </div>
          </div>
          <Button onClick={logout} variant="outline" className="rounded-full text-white border-white/20 hover:bg-white/10 hover:text-white" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </Button>
        </div>
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex gap-2 pb-3 overflow-x-auto no-scrollbar">
          {[
            { k: "stats", label: "Métricas", icon: TrendingUp },
            { k: "merchants", label: "Comercios", icon: Building2 },
            { k: "report", label: "Reporte / Asesores", icon: Briefcase },
            { k: "transfers", label: "Depósitos", icon: DollarSign },
            { k: "payments", label: "Pagos Reportados", icon: CreditCard },
            { k: "ai", label: "Gastos IA", icon: Bot },
            { k: "coupons", label: "Cupones", icon: Ticket },
            { k: "gold", label: "Cupón de Oro", icon: Trophy },
            { k: "raffle", label: "Sorteo mensual", icon: Gift },
            { k: "resets", label: "Reset Passwords", icon: KeyRound },
            { k: "matches", label: "Matches", icon: Users },
            { k: "pins", label: "PINs Gratis", icon: Ticket },
            { k: "account", label: "Mi cuenta", icon: KeyRound },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 transition-colors ${
                tab === t.k ? "bg-[#FF007A] text-white" : "bg-white/5 text-neutral-300 hover:bg-white/10"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        <div className="flex justify-end mb-4"><AuraVoiceToggle api={adminApi} /></div>
        {tab === "stats" && <StatsPanel />}
        {tab === "merchants" && <MerchantsPanel />}
        {tab === "report" && <MerchantsReportPanel />}
        {tab === "transfers" && <TransfersPanel />}
        {tab === "payments" && <PaymentReportsPanel />}
        {tab === "ai" && <AIUsagePanel />}
        {tab === "coupons" && <AdminCouponsPanel api={adminApi} />}
        {tab === "gold" && <AdminGoldPanel api={adminApi} />}
        {tab === "raffle" && <AdminRafflePanel api={adminApi} />}
        {tab === "resets" && <PasswordResetsPanel />}
        {tab === "matches" && <MatchesPanel />}
        {tab === "pins" && <PinsPanel />}
        {tab === "account" && <AdminAccountPanel admin={admin} />}
      </main>
    </div>
  );
}

// ---------- Panels ----------
function StatsPanel() {
  const [stats, setStats] = useState(null);
  const load = async () => {
    const { data } = await adminApi().get("/stats");
    setStats(data);
  };
  useEffect(() => { load(); }, []);

  if (!stats) return <div className="text-sm text-neutral-500">Cargando métricas…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-extrabold">Métricas globales</h2>
        <Button onClick={load} variant="outline" size="sm" className="rounded-full" data-testid="admin-refresh-stats"><RefreshCw className="w-3.5 h-3.5" /> Refrescar</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Comercios totales" value={stats.merchants.total} color="text-[#0055FF]" />
        <Stat label="Con radar activo" value={stats.merchants.active} color="text-emerald-600" />
        <Stat label="Suscritos (pagando)" value={stats.merchants.paying} color="text-[#FF007A]" />
        <Stat label="Usuarios anónimos" value={stats.users.anon_total} color="text-amber-500" />
        <Stat label="Matches totales" value={stats.matches.total} color="text-[#0055FF]" />
        <Stat label="Matches hoy" value={stats.matches.today} color="text-emerald-600" />
        <Stat label="Entregas" value={stats.redemptions.total} color="text-amber-500" />
        <Stat label="Depósitos pendientes" value={stats.bank_transfers.pending} color="text-red-500" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-white border border-neutral-200 p-6">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Ingresos por transferencias</div>
          <div className="font-heading font-black text-4xl mt-1 text-emerald-600">${(stats.revenue.bank_transfers_mxn || 0).toLocaleString("es-MX")} MXN</div>
        </div>
        <div className="rounded-2xl bg-white border border-neutral-200 p-6">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Ingresos Stripe</div>
          <div className="font-heading font-black text-4xl mt-1 text-[#0055FF]">${(stats.revenue.stripe_usd || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })} USD</div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-2xl bg-white border border-neutral-200 p-5">
      <div className={`w-2 h-2 rounded-full ${color?.replace("text-", "bg-") || "bg-neutral-300"} mb-3`} />
      <div className="text-xs text-neutral-500 font-medium">{label}</div>
      <div className="font-heading font-extrabold text-3xl mt-1" data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value ?? "—"}</div>
    </div>
  );
}

function MerchantsPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = async () => {
    const { data } = await adminApi().get("/merchants", { params: q ? { q } : {} });
    setRows(data.merchants);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- carga al montar / al cambiar filtros

  const toggleRadar = async (m) => {
    await adminApi().post(`/merchants/${m.merchant_id}/toggle-radar`);
    load();
  };
  const extend = async (m, days) => {
    await adminApi().post(`/merchants/${m.merchant_id}/extend-days`, { days });
    toast.success(`${days > 0 ? "+" : ""}${days} días aplicados`);
    load();
  };
  const remove = async (m) => {
    if (!window.confirm(`ELIMINAR ${m.business_name} y TODOS sus datos (ofertas, cupones, base de clientes, pagos)? Esta acción es irreversible.`)) return;
    try {
      const { data } = await adminApi().delete(`/merchants/${m.merchant_id}`);
      const n = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Comercio eliminado · ${n} registros relacionados borrados`);
      setRows((r) => r.filter((x) => x.merchant_id !== m.merchant_id));
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };
  const deactivate = async (m) => {
    const reason = window.prompt("Motivo de desactivación (pago no confirmado, fraude, etc.):", "Pago no confirmado");
    if (reason === null) return;
    try {
      await adminApi().post(`/merchants/${m.merchant_id}/deactivate`, { reason });
      toast.success("Suscripción desactivada · radar apagado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo desactivar"); }
  };
  const suspend = async (m) => {
    if (m.account_status === "suspended") {
      if (!window.confirm(`Reactivar la cuenta de ${m.business_name}?`)) return;
      try { await adminApi().post(`/merchants/${m.merchant_id}/unsuspend`); toast.success("Cuenta reactivada"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
      return;
    }
    const reason = window.prompt(`DAR DE BAJA a ${m.business_name}: el comercio ya no podrá entrar a su cuenta ni aparecer en el radar. Motivo:`);
    if (reason === null) return;
    try { await adminApi().post(`/merchants/${m.merchant_id}/suspend`, { reason }); toast.success("Comercio dado de baja"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const setPlan = async (m, plan_id) => {
    if (!plan_id || plan_id === m.plan_id) return;
    try { await adminApi().post(`/merchants/${m.merchant_id}/set-plan`, { plan_id }); toast.success("Paquete actualizado"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const toggleAI = async (m) => {
    const willDisable = !m.ai_disabled;
    const confirmMsg = willDisable
      ? `Desactivar la IA de "${m.business_name}"? El chat con IA y el simulador dejarán de funcionar hasta que la reactives.`
      : `Reactivar la IA de "${m.business_name}"?`;
    if (!window.confirm(confirmMsg)) return;
    await adminApi().post(`/merchants/${m.merchant_id}/toggle-ai`);
    toast.success(willDisable ? "IA desactivada" : "IA reactivada");
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar por nombre, tel, email…" className="pl-9 h-10 rounded-full" data-testid="admin-merchants-search" />
        </div>
        <Button onClick={load} variant="outline" className="rounded-full">Buscar</Button>
      </div>

      <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] text-xs uppercase tracking-widest text-neutral-500">
            <tr>
              <th className="text-left px-5 py-3 font-bold">Comercio</th>
              <th className="text-left px-5 py-3 font-bold">Suscripción</th>
              <th className="text-left px-5 py-3 font-bold">Radar / IA</th>
              <th className="text-left px-5 py-3 font-bold">Contacto</th>
              <th className="text-right px-5 py-3 font-bold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.merchant_id} className="border-t border-neutral-100" data-testid={`admin-merchant-${m.merchant_id}`}>
                <td className="px-5 py-3">
                  <div className="font-bold">{m.business_name}</div>
                  <div className="text-xs text-neutral-500 capitalize">{m.category}</div>
                </td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                    m.subscription?.status === "active" ? "bg-emerald-50 text-emerald-700" :
                    m.subscription?.status === "trialing" ? "bg-blue-50 text-blue-700" :
                    "bg-red-50 text-red-700"
                  }`}>
                    {m.subscription?.status}
                  </span>
                  <div className="text-[11px] text-neutral-500 mt-0.5">{m.subscription?.days_remaining ?? 0} días</div>
                  {m.account_status === "suspended" && <div className="mt-1 text-[10px] font-extrabold uppercase tracking-widest text-red-600" data-testid={`admin-suspended-${m.merchant_id}`}>BAJA</div>}
                  <select value={m.subscription?.tier === "cupones" ? "cupones_monthly_mxn" : "pro_monthly_mxn"} onChange={(e) => setPlan(m, e.target.value)} className={`mt-1 text-[10px] font-extrabold uppercase tracking-widest rounded-full border px-2 py-0.5 ${m.subscription?.tier === "cupones" ? "bg-[#FF007A]/10 text-[#FF007A] border-[#FF007A]/30" : "bg-[#0055FF]/10 text-[#0055FF] border-[#0055FF]/30"}`} data-testid={`admin-plan-${m.merchant_id}`} title="Cambiar paquete (sin cobro)">
                    <option value="cupones_monthly_mxn">Cupones $299</option>
                    <option value="pro_monthly_mxn">HOLA RADAR $990</option>
                  </select>
                </td>
                <td className="px-5 py-3">
                  <button onClick={() => toggleRadar(m)} className={`text-xs font-bold px-2 py-1 rounded-full ${m.radar_active ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`} data-testid={`admin-toggle-radar-${m.merchant_id}`}>
                    <RadarIcon className="inline w-3 h-3 mr-1" /> {m.radar_active ? "ON" : "OFF"}
                  </button>
                  <button onClick={() => toggleAI(m)} className={`ml-1 text-xs font-bold px-2 py-1 rounded-full ${m.ai_disabled ? "bg-red-50 text-red-600" : "bg-indigo-50 text-indigo-700"}`} data-testid={`admin-toggle-ai-${m.merchant_id}`} title={m.ai_disabled ? "IA desactivada — clic para reactivar" : "IA activa — clic para desactivar"}>
                    {m.ai_disabled ? <BotOff className="inline w-3 h-3 mr-1" /> : <Bot className="inline w-3 h-3 mr-1" />}
                    IA {m.ai_disabled ? "OFF" : "ON"}
                  </button>
                </td>
                <td className="px-5 py-3 text-xs text-neutral-600">{m.phone || m.email || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => extend(m, 30)} className="w-7 h-7 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center hover:bg-emerald-100" title="+30 días" data-testid={`admin-extend-${m.merchant_id}`}><Plus className="w-3.5 h-3.5" /></button>
                    <button onClick={() => extend(m, -30)} className="w-7 h-7 rounded-full bg-amber-50 text-amber-600 grid place-items-center hover:bg-amber-100" title="-30 días"><Minus className="w-3.5 h-3.5" /></button>
                    <button onClick={() => deactivate(m)} className="w-7 h-7 rounded-full bg-orange-50 text-orange-600 grid place-items-center hover:bg-orange-100" title="Desactivar (pago no confirmado)" data-testid={`admin-deactivate-${m.merchant_id}`}><Power className="w-3.5 h-3.5" /></button>
                    <button onClick={() => suspend(m)} className={`w-7 h-7 rounded-full grid place-items-center ${m.account_status === "suspended" ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"}`} title={m.account_status === "suspended" ? "Reactivar cuenta" : "Dar de baja (bloquea acceso)"} data-testid={`admin-suspend-${m.merchant_id}`}>{m.account_status === "suspended" ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}</button>
                    <button onClick={() => remove(m)} className="w-7 h-7 rounded-full bg-red-50 text-red-600 grid place-items-center hover:bg-red-100" title="Eliminar definitivamente" data-testid={`admin-delete-${m.merchant_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="text-center px-5 py-10 text-neutral-400 text-sm">Sin comercios</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TransfersPanel() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = async () => {
    const { data } = await adminApi().get("/transfers", { params: status ? { status } : {} });
    setRows(data.transfers);
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps -- carga al montar / al cambiar filtros

  const approve = async (t) => {
    if (!window.confirm(`Aprobar depósito de ${t.business_name}? Extenderá 30 días de suscripción.`)) return;
    await adminApi().post(`/transfers/${t.transfer_id}/approve`);
    toast.success("Aprobado. Suscripción extendida 30 días.");
    load();
  };
  const reject = async (t) => {
    const reason = window.prompt("Motivo del rechazo:");
    if (reason === null) return;
    await adminApi().post(`/transfers/${t.transfer_id}/reject`, { reason });
    toast.success("Rechazado");
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {["pending", "approved", "rejected", ""].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-xs font-bold ${status === s ? "bg-black text-white" : "bg-white border border-neutral-200 text-neutral-600"}`} data-testid={`admin-transfers-filter-${s || "all"}`}>
            {s === "" ? "Todos" : s === "pending" ? "Pendientes" : s === "approved" ? "Aprobados" : "Rechazados"}
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] text-xs uppercase tracking-widest text-neutral-500">
            <tr>
              <th className="text-left px-5 py-3 font-bold">Comercio</th>
              <th className="text-left px-5 py-3 font-bold">Referencia</th>
              <th className="text-left px-5 py-3 font-bold">Depositante</th>
              <th className="text-left px-5 py-3 font-bold">Monto</th>
              <th className="text-left px-5 py-3 font-bold">Estado</th>
              <th className="text-right px-5 py-3 font-bold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.transfer_id} className="border-t border-neutral-100" data-testid={`admin-transfer-${t.transfer_id}`}>
                <td className="px-5 py-3 font-bold">{t.business_name || t.merchant_id}</td>
                <td className="px-5 py-3 font-mono text-xs">{t.reference}</td>
                <td className="px-5 py-3">{t.depositor_name}</td>
                <td className="px-5 py-3 font-bold">${t.amount_mxn?.toFixed(0)} MXN</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                    t.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                    t.status === "rejected" ? "bg-red-50 text-red-700" :
                    "bg-amber-50 text-amber-700"
                  }`}>{t.status}</span>
                </td>
                <td className="px-5 py-3 text-right">
                  {t.status === "pending" && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => approve(t)} className="w-8 h-8 rounded-full bg-emerald-500 text-white grid place-items-center hover:bg-emerald-600" data-testid={`admin-approve-${t.transfer_id}`}><Check className="w-4 h-4" /></button>
                      <button onClick={() => reject(t)} className="w-8 h-8 rounded-full bg-red-500 text-white grid place-items-center hover:bg-red-600" data-testid={`admin-reject-${t.transfer_id}`}><X className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center px-5 py-10 text-neutral-400 text-sm">Sin depósitos {status}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MatchesPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => {
      const { data } = await adminApi().get("/recent-matches");
      setRows(data.matches);
    })();
  }, []);

  return (
    <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#FAFAFA] text-xs uppercase tracking-widest text-neutral-500">
          <tr>
            <th className="text-left px-5 py-3 font-bold">Fecha</th>
            <th className="text-left px-5 py-3 font-bold">Comercio</th>
            <th className="text-left px-5 py-3 font-bold">Usuario (device)</th>
            <th className="text-left px-5 py-3 font-bold">Distancia</th>
            <th className="text-left px-5 py-3 font-bold">Interés</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.match_id} className="border-t border-neutral-100">
              <td className="px-5 py-3 text-xs text-neutral-600">{new Date(r.created_at).toLocaleString("es-MX")}</td>
              <td className="px-5 py-3 font-mono text-xs">{r.merchant_id}</td>
              <td className="px-5 py-3 font-mono text-xs">{r.device_id}</td>
              <td className="px-5 py-3">{Math.round(r.distance_m)}m</td>
              <td className="px-5 py-3">
                {r.interested ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-neutral-400">—</span>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="text-center px-5 py-10 text-neutral-400 text-sm">Sin matches</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------------- PINs Panel ----------------
function PinsPanel() {
  const [pins, setPins] = useState([]);
  const [stats, setStats] = useState({ total: 0, used: 0, unused: 0 });
  const [filter, setFilter] = useState("all"); // all | unused | used
  const [loading, setLoading] = useState(false);
  const [gen, setGen] = useState({ quantity: 5, grant_days: 30, notes: "" });
  const [generating, setGenerating] = useState(false);
  const [lastCreated, setLastCreated] = useState([]);

  const load = async (nextFilter = filter) => {
    setLoading(true);
    try {
      const params = nextFilter === "all" ? {} : { params: { status: nextFilter } };
      const { data } = await adminApi().get("/pins", params);
      setPins(data.pins || []);
      setStats(data.stats || { total: 0, used: 0, unused: 0 });
    } catch (e) {
      toast.error("No se pudieron cargar los PINs");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- carga al montar / al cambiar filtros

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await adminApi().post("/pins", {
        quantity: parseInt(gen.quantity) || 1,
        grant_days: parseInt(gen.grant_days) || 30,
        notes: gen.notes || null,
      });
      setLastCreated(data.created || []);
      toast.success(`${data.count} PIN${data.count === 1 ? "" : "s"} generado${data.count === 1 ? "" : "s"}. Guárdalos ahora.`);
      load(filter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron generar PINs");
    } finally {
      setGenerating(false);
    }
  };

  const copyPin = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copiado: ${code}`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-pins-panel">
      {/* Stats + generator */}
      <div className="grid md:grid-cols-[1.1fr_2fr] gap-5">
        {/* Stats */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
          <div className="text-xs uppercase tracking-widest text-neutral-400 mb-3">Estado global</div>
          <div className="grid grid-cols-3 gap-3">
            <StatMini label="Total" value={stats.total} />
            <StatMini label="Sin usar" value={stats.unused} tone="green" />
            <StatMini label="Usados" value={stats.used} tone="pink" />
          </div>
          <div className="mt-4 flex gap-2 flex-wrap">
            {[
              { k: "all", label: "Todos" },
              { k: "unused", label: "Sin usar" },
              { k: "used", label: "Usados" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => { setFilter(f.k); load(f.k); }}
                data-testid={`pins-filter-${f.k}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                  filter === f.k ? "bg-[#FF007A] text-white" : "bg-white/5 text-neutral-300 hover:bg-white/10"
                }`}
              >
                {f.label}
              </button>
            ))}
            <button
              onClick={() => load(filter)}
              className="ml-auto text-neutral-400 hover:text-white text-xs flex items-center gap-1"
              data-testid="pins-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Actualizar
            </button>
          </div>
        </div>

        {/* Generator */}
        <div className="rounded-2xl bg-gradient-to-br from-[#FF007A]/15 to-[#0055FF]/15 border border-[#FF007A]/30 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Ticket className="w-4 h-4 text-[#FF007A]" />
            <span className="font-heading font-extrabold text-sm">Generar PINs de acceso gratuito</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400">Cantidad</label>
              <Input
                type="number"
                min={1} max={50}
                value={gen.quantity}
                onChange={(e) => setGen({ ...gen, quantity: e.target.value })}
                className="h-10 rounded-xl bg-white/5 border-white/10 text-white"
                data-testid="pins-gen-quantity"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400">Días</label>
              <Input
                type="number"
                min={1} max={365}
                value={gen.grant_days}
                onChange={(e) => setGen({ ...gen, grant_days: e.target.value })}
                className="h-10 rounded-xl bg-white/5 border-white/10 text-white"
                data-testid="pins-gen-days"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400">Nota (opcional)</label>
              <Input
                value={gen.notes}
                onChange={(e) => setGen({ ...gen, notes: e.target.value })}
                placeholder="Ej: Influencer, campaña Feb..."
                className="h-10 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-neutral-500"
                data-testid="pins-gen-notes"
              />
            </div>
            <Button
              onClick={generate}
              disabled={generating}
              className="h-10 rounded-full bg-[#FF007A] hover:bg-[#e5006e] font-bold"
              data-testid="pins-gen-submit"
            >
              {generating ? "..." : <><Plus className="w-4 h-4" /> Generar</>}
            </Button>
          </div>

          {lastCreated.length > 0 && (
            <div className="mt-4 rounded-xl bg-black/40 p-3 border border-[#FF007A]/20" data-testid="pins-last-created">
              <div className="text-[10px] uppercase tracking-widest text-[#FF007A] font-bold mb-2">
                ⚡ Recién creados — cópialos ahora
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {lastCreated.map((p) => (
                  <button
                    key={p.pin_id}
                    onClick={() => copyPin(p.pin_code)}
                    className="flex items-center justify-between rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 text-left transition-colors"
                  >
                    <span className="font-mono text-sm tracking-widest text-white">{p.pin_code}</span>
                    <Copy className="w-3.5 h-3.5 text-neutral-400" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left px-4 py-3 font-bold text-neutral-400 uppercase text-[10px] tracking-widest">PIN</th>
              <th className="text-left px-4 py-3 font-bold text-neutral-400 uppercase text-[10px] tracking-widest">Estado</th>
              <th className="text-left px-4 py-3 font-bold text-neutral-400 uppercase text-[10px] tracking-widest">Comercio</th>
              <th className="text-left px-4 py-3 font-bold text-neutral-400 uppercase text-[10px] tracking-widest">Canjeado</th>
              <th className="text-left px-4 py-3 font-bold text-neutral-400 uppercase text-[10px] tracking-widest">Válido hasta</th>
              <th className="text-left px-4 py-3 font-bold text-neutral-400 uppercase text-[10px] tracking-widest">Nota</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {pins.map((p) => {
              const used = !!p.used_by_merchant_id;
              const m = p.merchant || {};
              return (
                <tr key={p.pin_id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`pin-row-${p.pin_id}`}>
                  <td className="px-4 py-3 font-mono text-white tracking-widest">{p.pin_code}</td>
                  <td className="px-4 py-3">
                    {used ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF007A]/20 text-[#FF7AB8] text-[11px] font-bold">
                        <Check className="w-3 h-3" /> Usado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[11px] font-bold">
                        <Ticket className="w-3 h-3" /> Sin usar
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-neutral-200">
                    {used ? (
                      <div>
                        <div className="font-semibold">{m.business_name || "—"}</div>
                        <div className="text-[11px] text-neutral-500">{m.phone || "—"} · {m.category || ""}</div>
                      </div>
                    ) : (
                      <span className="text-neutral-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-neutral-300 text-[12px]">
                    {p.used_at ? new Date(p.used_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—"}
                  </td>
                  <td className="px-4 py-3 text-neutral-300 text-[12px]">
                    {p.activated_until ? new Date(p.activated_until).toLocaleDateString("es-MX", { dateStyle: "medium" }) : `+${p.grant_days || 30}d al canjear`}
                  </td>
                  <td className="px-4 py-3 text-neutral-400 text-[12px] max-w-[180px] truncate" title={p.notes || ""}>{p.notes || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {!used && (
                      <button onClick={() => copyPin(p.pin_code)} className="text-neutral-400 hover:text-white" title="Copiar PIN">
                        <Copy className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {pins.length === 0 && !loading && (
              <tr><td colSpan={7} className="text-center px-5 py-10 text-neutral-400 text-sm">No hay PINs con este filtro. Genera algunos arriba.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatMini({ label, value, tone }) {
  const toneClass = tone === "green" ? "text-emerald-400" : tone === "pink" ? "text-[#FF7AB8]" : "text-white";
  return (
    <div className="rounded-xl bg-black/30 border border-white/5 p-3">
      <div className={`font-heading font-black text-2xl leading-none ${toneClass}`}>{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">{label}</div>
    </div>
  );
}


// ---------------- Payment Reports Panel (Card/OXXO reports) ----------------
function PaymentReportsPanel() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending_verification");
  const [loading, setLoading] = useState(false);

  const load = async (s = status) => {
    setLoading(true);
    try {
      const params = s ? { params: { status: s } } : {};
      const { data } = await adminApi().get("/payment-reports", params);
      setRows(data.reports || []);
    } catch {
      toast.error("No se pudieron cargar los pagos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(status); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps -- carga al montar / al cambiar filtros

  const verify = async (r) => {
    if (!window.confirm(`Marcar como VERIFICADO el pago ${r.method} de ${r.business_name}?`)) return;
    try {
      await adminApi().post(`/payment-reports/${r.report_id}/verify`);
      toast.success("Pago verificado");
      load(status);
    } catch {
      toast.error("No se pudo verificar");
    }
  };

  const reject = async (r) => {
    const reason = window.prompt(
      `⚠️ RECHAZAR pago y DESACTIVAR el comercio ${r.business_name}?\n\nEscribe el motivo (visible en logs):`
    );
    if (reason === null) return;
    if (reason.trim().length < 5) {
      toast.error("Escribe un motivo de al menos 5 caracteres");
      return;
    }
    try {
      await adminApi().post(`/payment-reports/${r.report_id}/reject`, { reason: reason.trim() });
      toast.success("Pago rechazado. Comercio desactivado.");
      load(status);
    } catch {
      toast.error("No se pudo rechazar");
    }
  };

  const statusBadge = (s) => {
    if (s === "verified") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold"><Check className="w-3 h-3" /> Verificado</span>;
    if (s === "rejected") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] font-bold"><X className="w-3 h-3" /> Rechazado</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold"><RefreshCw className="w-3 h-3" /> Pendiente</span>;
  };

  const methodLabel = (m) => (m === "stripe_card" ? "Tarjeta" : m === "oxxo" ? "OXXO" : m || "?");

  return (
    <div className="space-y-4" data-testid="admin-payments-panel">
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { k: "pending_verification", label: "Pendientes" },
          { k: "verified", label: "Verificados" },
          { k: "rejected", label: "Rechazados" },
          { k: "", label: "Todos" },
        ].map((s) => (
          <button
            key={s.k || "all"}
            onClick={() => setStatus(s.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${status === s.k ? "bg-black text-white" : "bg-white border border-neutral-200 text-neutral-600"}`}
            data-testid={`admin-payments-filter-${s.k || "all"}`}
          >
            {s.label}
          </button>
        ))}
        <button onClick={() => load(status)} className="ml-auto text-neutral-500 hover:text-black text-xs flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Actualizar
        </button>
      </div>

      <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] text-xs uppercase tracking-widest text-neutral-500">
            <tr>
              <th className="text-left px-5 py-3 font-bold">Comercio</th>
              <th className="text-left px-5 py-3 font-bold">Método</th>
              <th className="text-left px-5 py-3 font-bold">Referencia / Folio</th>
              <th className="text-left px-5 py-3 font-bold">Titular</th>
              <th className="text-left px-5 py-3 font-bold">Monto</th>
              <th className="text-left px-5 py-3 font-bold">Fecha reporte</th>
              <th className="text-left px-5 py-3 font-bold">Estado</th>
              <th className="text-right px-5 py-3 font-bold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.report_id} className="border-t border-neutral-100" data-testid={`admin-payment-row-${r.report_id}`}>
                <td className="px-5 py-3 font-bold">{r.business_name || r.merchant_id}</td>
                <td className="px-5 py-3">{methodLabel(r.method)}</td>
                <td className="px-5 py-3 font-mono text-xs">{r.reference}</td>
                <td className="px-5 py-3">{r.payer_name || "—"}</td>
                <td className="px-5 py-3 font-bold">{r.amount ? `$${Number(r.amount).toFixed(0)} ${(r.currency || "MXN").toUpperCase()}` : "—"}</td>
                <td className="px-5 py-3 text-xs text-neutral-500">
                  {r.created_at ? new Date(r.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—"}
                </td>
                <td className="px-5 py-3">{statusBadge(r.status)}</td>
                <td className="px-5 py-3 text-right">
                  {r.status === "pending_verification" && (
                    <div className="inline-flex gap-2">
                      <Button onClick={() => verify(r)} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs" data-testid={`payment-verify-${r.report_id}`}>
                        <Check className="w-3.5 h-3.5" /> Verificar
                      </Button>
                      <Button onClick={() => reject(r)} size="sm" variant="outline" className="rounded-full border-red-300 text-red-600 hover:bg-red-50 h-8 px-3 text-xs" data-testid={`payment-reject-${r.report_id}`}>
                        <X className="w-3.5 h-3.5" /> Rechazar
                      </Button>
                    </div>
                  )}
                  {r.status === "rejected" && r.reject_reason && (
                    <span className="text-xs text-red-600 max-w-[220px] block truncate" title={r.reject_reason}>{r.reject_reason}</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={8} className="text-center px-5 py-10 text-neutral-400 text-sm">No hay pagos reportados con este filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ---------- Reporte de gastos IA (mensual) ----------
function currentMonthYM() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatMxn(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const abs = Math.abs(v);
  const digits = abs < 0.01 ? 4 : abs < 1 ? 3 : 2;
  return `$${v.toFixed(digits)}`;
}

function AIUsagePanel() {
  const [month, setMonth] = useState(currentMonthYM());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/ai-usage-report", { params: { month } });
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar el reporte");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps -- carga al montar / al cambiar filtros

  const toggleAI = async (row) => {
    const willDisable = !row.ai_disabled;
    const msg = willDisable
      ? `¿Desactivar la IA de "${row.business_name}"? El chat y simulador dejarán de responder.`
      : `¿Reactivar la IA de "${row.business_name}"?`;
    if (!window.confirm(msg)) return;
    await adminApi().post(`/merchants/${row.merchant_id}/toggle-ai`);
    toast.success(willDisable ? "IA desactivada" : "IA reactivada");
    load();
  };

  const exportCSV = () => {
    if (!report?.rows?.length) return;
    const header = ["merchant_id", "business_name", "category", "turns", "sim_turns", "chat_turns", "input_tokens", "output_tokens", "cost_usd", "cost_mxn", "ai_disabled"];
    const lines = [header.join(",")];
    for (const r of report.rows) {
      lines.push([
        r.merchant_id,
        `"${(r.business_name || "").replace(/"/g, '""')}"`,
        r.category || "",
        r.turns,
        r.sim_turns,
        r.chat_turns,
        r.input_tokens,
        r.output_tokens,
        r.cost_usd,
        r.cost_mxn,
        r.ai_disabled ? "yes" : "no",
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `gastos-ia-${report.month}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const totals = report?.totals || { turns: 0, input_tokens: 0, output_tokens: 0, cost_usd: 0, cost_mxn: 0 };
  const pricing = report?.pricing;

  return (
    <div className="space-y-5" data-testid="admin-ai-usage-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Reporte mensual</div>
          <div className="font-heading font-extrabold text-2xl">Gastos IA por comercio</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border border-neutral-300 rounded-full px-3 py-2 text-sm"
            data-testid="admin-ai-month"
          />
          <Button onClick={load} variant="outline" className="rounded-full" data-testid="admin-ai-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refrescar
          </Button>
          <Button onClick={exportCSV} disabled={!report?.rows?.length} className="rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold" data-testid="admin-ai-export">
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <TotalCard label="Turnos totales" value={totals.turns.toLocaleString()} />
        <TotalCard label="Tokens entrada" value={totals.input_tokens.toLocaleString()} />
        <TotalCard label="Tokens salida" value={totals.output_tokens.toLocaleString()} />
        <TotalCard label="Costo total mes" value={`${formatMxn(totals.cost_mxn)} MXN`} highlight />
      </div>
      {pricing && (
        <p className="text-[11px] text-neutral-500 leading-snug">
          Tarifas: entrada ${pricing.input_usd_per_1m}/1M tokens · salida ${pricing.output_usd_per_1m}/1M tokens ({pricing.model}). Tipo de cambio referencial: 1 USD ≈ ${pricing.mxn_per_usd} MXN. Costos incluyen chats con clientes (chat) y pruebas del comerciante (sim).
        </p>
      )}

      {/* Table */}
      <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] text-xs uppercase tracking-widest text-neutral-500">
            <tr>
              <th className="text-left px-4 py-3 font-bold">Comercio</th>
              <th className="text-right px-4 py-3 font-bold">Turnos</th>
              <th className="text-right px-4 py-3 font-bold">Chat / Sim</th>
              <th className="text-right px-4 py-3 font-bold">Tokens IN</th>
              <th className="text-right px-4 py-3 font-bold">Tokens OUT</th>
              <th className="text-right px-4 py-3 font-bold">Costo MXN</th>
              <th className="text-center px-4 py-3 font-bold">IA</th>
              <th className="text-right px-4 py-3 font-bold">Acción</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center px-4 py-10 text-neutral-400 text-sm">Cargando…</td></tr>
            )}
            {!loading && report?.rows?.map((r) => (
              <tr key={r.merchant_id} className="border-t border-neutral-100" data-testid={`admin-ai-row-${r.merchant_id}`}>
                <td className="px-4 py-3">
                  <div className="font-bold">{r.business_name}</div>
                  <div className="text-xs text-neutral-500 capitalize">{r.category || "—"} · {r.phone || r.email || "—"}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono">{r.turns.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-mono text-xs text-neutral-600">{r.chat_turns} / {r.sim_turns}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{r.input_tokens.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{r.output_tokens.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-heading font-extrabold text-emerald-700">{formatMxn(r.cost_mxn)}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.ai_disabled ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
                    {r.ai_disabled ? "OFF" : "ON"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    onClick={() => toggleAI(r)}
                    size="sm"
                    variant={r.ai_disabled ? "outline" : "default"}
                    className={`rounded-full h-8 px-3 text-xs font-bold ${r.ai_disabled ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "bg-red-600 hover:bg-red-700 text-white"}`}
                    data-testid={`admin-ai-toggle-${r.merchant_id}`}
                  >
                    {r.ai_disabled ? <><Bot className="w-3.5 h-3.5" /> Reactivar IA</> : <><BotOff className="w-3.5 h-3.5" /> Inactivar IA</>}
                  </Button>
                </td>
              </tr>
            ))}
            {!loading && (!report?.rows || report.rows.length === 0) && (
              <tr><td colSpan={8} className="text-center px-4 py-10 text-neutral-400 text-sm">Sin consumo de IA registrado en {month}.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TotalCard({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? "bg-emerald-50 border-emerald-200" : "bg-white border-neutral-200"}`}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
      <div className={`text-xl md:text-2xl font-heading font-extrabold mt-1 ${highlight ? "text-emerald-700" : "text-neutral-900"}`}>{value}</div>
    </div>
  );
}

// ---------- Panel de Reset de Contraseñas ----------
function PasswordResetsPanel() {
  const [resets, setResets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [approved, setApproved] = useState(null); // {temporary_password, business_name, merchant_id}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/password-resets", { params: { status: statusFilter } });
      setResets(data.resets || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar la lista");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps -- carga al montar / al cambiar filtros

  const approve = async (r) => {
    const custom = window.prompt(
      `Aprobar reset para "${r.business_name}" (tel ${r.phone}).\nDeja vacío para generar una contraseña temporal automáticamente, o escribe una tú:`,
      "",
    );
    if (custom === null) return;
    try {
      const { data } = await adminApi().post(`/password-resets/${r.reset_id}/approve`, { new_password: custom || undefined });
      setApproved(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo aprobar");
    }
  };
  const reject = async (r) => {
    if (!window.confirm(`¿Rechazar la solicitud de "${r.business_name}"?`)) return;
    try {
      await adminApi().post(`/password-resets/${r.reset_id}/reject`);
      toast.success("Solicitud rechazada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo rechazar");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-resets-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Solicitudes</div>
          <div className="font-heading font-extrabold text-2xl">Reset de contraseñas</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-neutral-300 rounded-full px-3 py-2 text-sm bg-white"
            data-testid="admin-resets-filter"
          >
            <option value="pending">Pendientes</option>
            <option value="approved">Aprobadas</option>
            <option value="rejected">Rechazadas</option>
            <option value="all">Todas</option>
          </select>
          <Button onClick={load} variant="outline" className="rounded-full" data-testid="admin-resets-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refrescar
          </Button>
        </div>
      </div>

      {/* Modal simple para mostrar la contraseña temporal generada */}
      {approved && (
        <div className="rounded-2xl border-2 border-emerald-400 bg-emerald-50 p-5 space-y-3" data-testid="admin-reset-approved-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-600 text-white grid place-items-center shrink-0">
              <Check className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-heading font-extrabold text-lg">Contraseña temporal generada</div>
              <div className="text-xs text-neutral-600 mt-1">Copia esta contraseña y compártela con <strong>{approved.business_name}</strong> por WhatsApp o llamada. Esta es la única vez que la verás.</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-xl bg-white border-2 border-emerald-300 px-4 py-3 font-mono text-2xl font-extrabold tracking-widest text-emerald-800 text-center" data-testid="admin-reset-temp-pw">
              {approved.temporary_password}
            </div>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(approved.temporary_password);
                toast.success("Copiado");
              }}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12"
              data-testid="admin-reset-copy"
            >
              <Copy className="w-4 h-4" /> Copiar
            </Button>
          </div>
          <Button onClick={() => setApproved(null)} variant="outline" className="rounded-full w-full" data-testid="admin-reset-dismiss">
            Ya la compartí, cerrar
          </Button>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] text-xs uppercase tracking-widest text-neutral-500">
            <tr>
              <th className="text-left px-4 py-3 font-bold">Comercio</th>
              <th className="text-left px-4 py-3 font-bold">Celular</th>
              <th className="text-left px-4 py-3 font-bold">Solicitado</th>
              <th className="text-center px-4 py-3 font-bold">Estado</th>
              <th className="text-right px-4 py-3 font-bold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="text-center py-10 text-neutral-400">Cargando…</td></tr>}
            {!loading && resets.map((r) => (
              <tr key={r.reset_id} className="border-t border-neutral-100" data-testid={`admin-reset-row-${r.reset_id}`}>
                <td className="px-4 py-3 font-semibold">{r.business_name || "(sin nombre)"}</td>
                <td className="px-4 py-3 font-mono">{r.phone}</td>
                <td className="px-4 py-3 text-xs text-neutral-500">{new Date(r.created_at).toLocaleString("es-MX")}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                    r.status === "pending" ? "bg-amber-50 text-amber-700" :
                    r.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                    "bg-neutral-100 text-neutral-500"
                  }`}>
                    {r.status.toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {r.status === "pending" && (
                    <div className="flex gap-1 justify-end">
                      <Button onClick={() => approve(r)} size="sm" className="rounded-full h-8 px-3 text-xs font-bold bg-emerald-600 hover:bg-emerald-700" data-testid={`admin-reset-approve-${r.reset_id}`}>
                        <Check className="w-3.5 h-3.5" /> Aprobar
                      </Button>
                      <Button onClick={() => reject(r)} size="sm" variant="outline" className="rounded-full h-8 px-3 text-xs font-bold border-neutral-300" data-testid={`admin-reset-reject-${r.reset_id}`}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!loading && resets.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-neutral-400 text-sm">Sin solicitudes en este estado.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ---------- Mi cuenta (admin) ----------
function AdminAccountPanel({ admin }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const strength = passwordStrength(next);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (next !== confirm) {
      toast.error("La confirmación no coincide con la nueva contraseña");
      return;
    }
    if (next === current) {
      toast.error("La nueva contraseña debe ser distinta a la actual");
      return;
    }
    setSaving(true);
    try {
      await adminApi().post("/change-password", { current_password: current, new_password: next });
      toast.success("Contraseña actualizada. En 5s te vuelvo a pedir login por seguridad.");
      setCurrent(""); setNext(""); setConfirm("");
      setTimeout(() => {
        localStorage.removeItem("holaradar_admin_token");
        window.location.href = "/admin/login";
      }, 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar la contraseña");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-account-panel">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Mi cuenta</div>
        <div className="font-heading font-extrabold text-2xl">Administrador</div>
        <p className="text-sm text-neutral-600 mt-1">Actualiza tu contraseña de acceso al panel administrativo.</p>
      </div>

      <div className="rounded-2xl bg-white border border-neutral-200 p-6 space-y-1">
        <InfoRow label="Nombre" value={admin?.name || "—"} />
        <InfoRow label="Rol" value={(admin?.role || "").replace("_", " ")} />
        <InfoRow label="Celular" value={admin?.phone || "—"} />
      </div>

      <form onSubmit={submit} className="rounded-2xl bg-white border border-neutral-200 p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="w-4 h-4 text-[#0055FF]" />
            <h3 className="font-heading font-extrabold text-lg">Cambiar contraseña</h3>
          </div>
          <p className="text-xs text-neutral-500">Elige una contraseña larga con mezcla de mayúsculas, números y símbolos.</p>
        </div>
        <div>
          <Label htmlFor="admin-current-pw">Contraseña actual</Label>
          <PasswordInput
            id="admin-current-pw"
            testid="admin-current-pw"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            className="mt-1.5 h-11 rounded-xl"
            required
          />
        </div>
        <div>
          <Label htmlFor="admin-new-pw">Nueva contraseña <span className="text-neutral-400 font-normal">(mínimo 8)</span></Label>
          <PasswordInput
            id="admin-new-pw"
            testid="admin-new-pw"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            className="mt-1.5 h-11 rounded-xl"
            required
          />
          {next && <StrengthMeter score={strength.score} label={strength.label} />}
        </div>
        <div>
          <Label htmlFor="admin-confirm-pw">Confirmar nueva contraseña</Label>
          <PasswordInput
            id="admin-confirm-pw"
            testid="admin-confirm-pw"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            className="mt-1.5 h-11 rounded-xl"
            required
          />
          {confirm && confirm !== next && (
            <div className="text-xs text-red-600 mt-1">Las contraseñas no coinciden</div>
          )}
        </div>
        <Button
          type="submit"
          disabled={saving || !current || !next || !confirm}
          className="w-full h-11 rounded-full bg-[#FF007A] hover:bg-[#e6006e] font-bold"
          data-testid="admin-change-pw-btn"
        >
          <KeyRound className="w-4 h-4" /> {saving ? "Guardando…" : "Actualizar contraseña"}
        </Button>
        <p className="text-[11px] text-neutral-500 leading-snug">
          Por seguridad, al cambiar la contraseña tu sesión se cerrará automáticamente y tendrás que volver a entrar con la nueva.
        </p>
      </form>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-none">
      <span className="text-xs uppercase tracking-widest text-neutral-500 font-bold">{label}</span>
      <span className="text-sm font-semibold capitalize">{value}</span>
    </div>
  );
}

function passwordStrength(pw) {
  if (!pw) return { score: 0, label: "" };
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/[0-9]/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  const labels = ["Muy débil", "Débil", "Aceptable", "Buena", "Fuerte", "Excelente"];
  return { score, label: labels[Math.min(score, labels.length - 1)] };
}

function StrengthMeter({ score, label }) {
  const pct = Math.min(100, (score / 5) * 100);
  const color = score <= 1 ? "bg-red-500" : score === 2 ? "bg-amber-500" : score === 3 ? "bg-yellow-500" : "bg-emerald-500";
  return (
    <div className="mt-2" data-testid="admin-pw-strength">
      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[11px] text-neutral-600 mt-1">Fuerza: <strong>{label}</strong></div>
    </div>
  );
}


// ==============================================================
// Reporte de Comercios por Asesor (admin)
// ==============================================================
function MerchantsReportPanel() {
  const [report, setReport] = useState(null);
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [advisorFilter, setAdvisorFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (advisorFilter) params.advisor = advisorFilter;
      if (statusFilter !== "all") params.status_filter = statusFilter;
      const [rep, advs] = await Promise.all([
        adminApi().get("/merchants/report", { params }),
        adminApi().get("/advisors"),
      ]);
      setReport(rep.data);
      setAdvisors(advs.data.advisors || []);
    } catch {
      toast.error("No se pudo cargar el reporte");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [statusFilter, advisorFilter]); // eslint-disable-line react-hooks/exhaustive-deps -- carga al montar / al cambiar filtros

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const downloadCsv = () => {
    if (!report?.merchants) return;
    const headers = ["Comercio", "Asesor", "Teléfono", "Dirección", "Categoría", "Estatus", "Vence", "Días restantes"];
    const rows = report.merchants.map((m) => [
      m.business_name, m.advisor_name || "", m.phone || "", m.address || "", m.category || "",
      m.subscription_status, m.active_until ? new Date(m.active_until).toISOString().slice(0, 10) : "",
      m.days_remaining ?? "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `holaradar-comercios-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !report) return <div className="text-neutral-500 text-sm">Cargando reporte…</div>;
  if (!report) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Reporte administrativo</p>
          <h2 className="font-heading font-extrabold text-2xl md:text-3xl">Comercios por asesor + estatus de mensualidad</h2>
        </div>
        <Button onClick={downloadCsv} variant="outline" className="rounded-full text-xs h-9" data-testid="admin-report-csv">
          <Download className="w-3.5 h-3.5" /> Exportar CSV
        </Button>
      </div>

      {/* Stats globales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Total comercios" value={report.stats.total} icon={Building2} color="bg-blue-500/20 text-blue-300" />
        <MiniStat label="Al día" value={report.stats.active} icon={CheckCircle2} color="bg-green-500/20 text-green-300" />
        <MiniStat label="En prueba" value={report.stats.trialing} icon={Clock} color="bg-amber-500/20 text-amber-300" />
        <MiniStat label="Vencidos" value={report.stats.expired} icon={AlertTriangle} color="bg-red-500/20 text-red-300" />
      </div>

      {/* Resumen por asesor */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Briefcase className="w-4 h-4 text-[#FF007A]" />
          <h3 className="font-heading font-extrabold text-base">Ventas por asesor</h3>
          <span className="text-xs text-neutral-500 ml-1">({report.by_advisor.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/10">
                <th className="py-2 font-bold">Asesor</th>
                <th className="py-2 font-bold text-right">Total</th>
                <th className="py-2 font-bold text-right">Al día</th>
                <th className="py-2 font-bold text-right">En prueba</th>
                <th className="py-2 font-bold text-right">Vencidos</th>
                <th className="py-2 font-bold text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {report.by_advisor.map((a) => (
                <tr key={a.advisor_name} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2 font-bold">{a.advisor_name}</td>
                  <td className="py-2 text-right">{a.total}</td>
                  <td className="py-2 text-right text-green-400">{a.active || 0}</td>
                  <td className="py-2 text-right text-amber-400">{a.trialing || 0}</td>
                  <td className="py-2 text-right text-red-400">{a.expired || 0}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => setAdvisorFilter(a.advisor_name === "— sin asesor —" ? "" : a.advisor_name)}
                      className="text-[10px] text-[#FF007A] hover:underline font-bold"
                    >
                      Filtrar →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <form onSubmit={onSearch} className="flex-1 min-w-[220px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por comercio, teléfono o asesor…"
            className="pl-9 h-10 rounded-full bg-white/5 border-white/10 text-white placeholder:text-neutral-500"
            data-testid="admin-report-search"
          />
        </form>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-full bg-white/5 border border-white/10 text-white text-xs px-3"
          data-testid="admin-report-status-filter"
        >
          <option value="all">Todos los estatus</option>
          <option value="active">Solo al día</option>
          <option value="trialing">Solo en prueba</option>
          <option value="expired">Solo vencidos</option>
        </select>
        {advisorFilter && (
          <button
            onClick={() => setAdvisorFilter("")}
            className="text-xs px-3 h-10 rounded-full bg-[#FF007A]/20 text-[#FF007A] border border-[#FF007A]/40 flex items-center gap-1"
          >
            <X className="w-3 h-3" /> {advisorFilter}
          </button>
        )}
      </div>

      {/* Tabla detalle */}
      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-report-table">
            <thead className="bg-white/5">
              <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-400">
                <th className="p-3 font-bold">Comercio</th>
                <th className="p-3 font-bold">Asesor</th>
                <th className="p-3 font-bold">Contacto</th>
                <th className="p-3 font-bold">Estatus</th>
                <th className="p-3 font-bold text-right">Vence</th>
              </tr>
            </thead>
            <tbody>
              {report.merchants.map((m) => (
                <tr key={m.merchant_id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="p-3">
                    <div className="font-bold">{m.business_name}</div>
                    <div className="text-[10px] text-neutral-500">{m.category}</div>
                  </td>
                  <td className="p-3 text-neutral-300">{m.advisor_name || <span className="text-neutral-600 italic">— sin asesor —</span>}</td>
                  <td className="p-3 text-xs">
                    {m.phone && <div className="flex items-center gap-1 text-neutral-300"><Phone className="w-3 h-3" /> {m.phone}</div>}
                    {m.address && <div className="flex items-center gap-1 text-neutral-500 mt-0.5"><MapPin className="w-3 h-3" /> {m.address}</div>}
                  </td>
                  <td className="p-3"><StatusPill status={m.subscription_status} isTrial={m.is_trial} /></td>
                  <td className="p-3 text-right">
                    <div className="text-xs">{m.active_until ? new Date(m.active_until).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div>
                    <div className="text-[10px] text-neutral-500">{m.days_remaining > 0 ? `${m.days_remaining} días` : "vencido"}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {report.merchants.length === 0 && (
          <div className="p-6 text-center text-sm text-neutral-500">Ningún comercio coincide con estos filtros.</div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, color }) {
  return (
    <div className={`rounded-xl px-3 py-2 ${color} border border-white/10`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-80 flex items-center gap-1"><Icon className="w-3 h-3" />{label}</div>
      <div className="font-heading font-extrabold text-2xl leading-none mt-1">{value}</div>
    </div>
  );
}

function StatusPill({ status, isTrial }) {
  const map = {
    active: { label: "Al día", cls: "bg-green-500/20 text-green-300 border-green-500/40" },
    trialing: { label: "En prueba", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
    expired: { label: "Vencido", cls: "bg-red-500/20 text-red-300 border-red-500/40" },
  };
  const s = map[status] || map.expired;
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border ${s.cls}`}>
      {s.label}
    </span>
  );
}


